import { useEffect, useState } from 'react';
import { Link } from 'react-scroll';
import { motion, AnimatePresence } from 'framer-motion';

const links = ['About', 'Services', 'Projects', 'Testimonials', 'Contact'];

export default function Navbar() {
  const [scrolled, setScrolled]   = useState(false);
  const [menuOpen, setMenuOpen]   = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lock body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const close = () => setMenuOpen(false);

  return (
    <>
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 800,
        padding: '28px 80px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        background: scrolled ? 'rgba(8,8,8,0.97)' : 'linear-gradient(to bottom, rgba(8,8,8,0.95), transparent)',
        borderBottom: scrolled ? '1px solid rgba(255,255,255,0.05)' : '1px solid transparent',
        transition: 'all 0.4s ease',
      }}>
        {/* Logo */}
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '22px', fontWeight: 300, letterSpacing: '0.1em', color: '#c9a96e' }}>
          VR<span style={{ opacity: 0.35 }}>.</span>
        </div>

        {/* Desktop links */}
        <ul className="nav-desktop" style={{ display: 'flex', gap: '44px', listStyle: 'none' }}>
          {links.map(l => (
            <li key={l}>
              <Link to={l.toLowerCase()} smooth duration={800} offset={-80}
                style={{ fontSize: '11px', letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(245,240,232,0.45)', cursor: 'none', transition: 'color .3s' }}
                onMouseEnter={e => e.target.style.color = '#c9a96e'}
                onMouseLeave={e => e.target.style.color = 'rgba(245,240,232,0.45)'}
              >{l}</Link>
            </li>
          ))}
        </ul>

        {/* Unique hamburger — 3 lines with gold accent */}
        <button
          className="nav-mobile-btn"
          onClick={() => setMenuOpen(o => !o)}
          data-hover
          aria-label="Toggle menu"
          style={{
            display: 'none',
            flexDirection: 'column', justifyContent: 'center',
            gap: '0', padding: '8px', background: 'transparent',
            border: '1px solid rgba(201,169,110,0.2)',
            width: '44px', height: '44px', cursor: 'none',
            position: 'relative', overflow: 'hidden',
          }}
        >
          {/* Gold shimmer bg on hover */}
          <span style={{
            position: 'absolute', inset: 0,
            background: 'rgba(201,169,110,0.06)',
            opacity: 0, transition: 'opacity .3s',
          }} className="btn-bg" />

          {/* Line 1 */}
          <span style={{
            display: 'block', height: '1px',
            background: menuOpen ? '#c9a96e' : 'rgba(245,240,232,0.7)',
            transition: 'all 0.35s cubic-bezier(0.76,0,0.24,1)',
            transform: menuOpen ? 'translateY(9px) rotate(45deg)' : 'translateY(-5px)',
            transformOrigin: 'center',
            width: menuOpen ? '22px' : '22px',
            margin: '0 auto',
          }} />

          {/* Line 2 — middle (becomes invisible) */}
          <span style={{
            display: 'block', height: '1px',
            background: '#c9a96e',
            transition: 'all 0.25s ease',
            opacity: menuOpen ? 0 : 1,
            transform: menuOpen ? 'scaleX(0)' : 'scaleX(1)',
            width: '14px',
            margin: '0 auto',
          }} />

          {/* Line 3 */}
          <span style={{
            display: 'block', height: '1px',
            background: menuOpen ? '#c9a96e' : 'rgba(245,240,232,0.7)',
            transition: 'all 0.35s cubic-bezier(0.76,0,0.24,1)',
            transform: menuOpen ? 'translateY(-9px) rotate(-45deg)' : 'translateY(5px)',
            transformOrigin: 'center',
            width: '22px',
            margin: '0 auto',
          }} />
        </button>
      </nav>

      {/* ── Mobile fullscreen overlay ── */}
      <AnimatePresence>
        {menuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={close}
              style={{
                position: 'fixed', inset: 0, zIndex: 790,
                background: 'rgba(8,8,8,0.6)',
                backdropFilter: 'blur(4px)',
              }}
            />

            {/* Slide-in panel from right */}
            <motion.div
              key="panel"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.45, ease: [0.76, 0, 0.24, 1] }}
              style={{
                position: 'fixed', top: 0, right: 0, bottom: 0,
                width: 'min(320px, 85vw)',
                background: '#0d0d0d',
                borderLeft: '1px solid rgba(201,169,110,0.15)',
                zIndex: 795,
                display: 'flex', flexDirection: 'column',
                padding: '0',
                overflow: 'hidden',
              }}
            >
              {/* Panel top bar */}
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '24px 32px',
                borderBottom: '1px solid rgba(255,255,255,0.05)',
              }}>
                <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '18px', fontWeight: 300, color: '#c9a96e', letterSpacing: '0.1em' }}>
                  VR.
                </span>

                {/* ✕ Close button */}
                <button
                  onClick={close}
                  data-hover
                  aria-label="Close menu"
                  style={{
                    width: '36px', height: '36px',
                    border: '1px solid rgba(201,169,110,0.25)',
                    background: 'transparent',
                    color: '#c9a96e',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'none', transition: 'all .3s', flexShrink: 0,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#c9a96e'; e.currentTarget.style.color = '#080808'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#c9a96e'; }}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                    <path d="M1 1l12 12M13 1L1 13"/>
                  </svg>
                </button>
              </div>

              {/* Nav links */}
              <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0 32px', gap: '4px' }}>
                {links.map((l, i) => (
                  <motion.div
                    key={l}
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.15 + i * 0.07, ease: [0.25, 0.1, 0.25, 1] }}
                  >
                    <Link
                      to={l.toLowerCase()} smooth duration={800} offset={-80}
                      onClick={close}
                      style={{ display: 'block', textDecoration: 'none' }}
                    >
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '18px 0',
                        borderBottom: '1px solid rgba(255,255,255,0.04)',
                        cursor: 'none',
                      }}
                        onMouseEnter={e => {
                          e.currentTarget.querySelector('.link-num').style.color = '#c9a96e';
                          e.currentTarget.querySelector('.link-text').style.color = '#c9a96e';
                          e.currentTarget.querySelector('.link-arrow').style.opacity = '1';
                          e.currentTarget.querySelector('.link-arrow').style.transform = 'translateX(4px)';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.querySelector('.link-num').style.color = 'rgba(201,169,110,0.3)';
                          e.currentTarget.querySelector('.link-text').style.color = 'rgba(245,240,232,0.7)';
                          e.currentTarget.querySelector('.link-arrow').style.opacity = '0';
                          e.currentTarget.querySelector('.link-arrow').style.transform = 'translateX(0)';
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '16px' }}>
                          <span className="link-num" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '12px', color: 'rgba(201,169,110,0.3)', transition: 'color .3s' }}>
                            0{i + 1}
                          </span>
                          <span className="link-text" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', fontWeight: 300, color: 'rgba(245,240,232,0.7)', letterSpacing: '-0.01em', transition: 'color .3s' }}>
                            {l}
                          </span>
                        </div>
                        <span className="link-arrow" style={{ color: '#c9a96e', fontSize: '18px', opacity: 0, transition: 'opacity .3s, transform .3s' }}>→</span>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </nav>

              {/* Panel bottom */}
              <div style={{
                padding: '24px 32px',
                borderTop: '1px solid rgba(255,255,255,0.05)',
              }}>
                <a href="/resume.pdf" target="_blank" rel="noreferrer"
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    fontSize: '10px', letterSpacing: '0.22em', textTransform: 'uppercase',
                    color: '#c9a96e', textDecoration: 'none', fontFamily: "'Syne', sans-serif",
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
                    <rect x="2" y="1" width="9" height="12" rx="1"/>
                    <path d="M5 1v4h4M5 9h4M5 11h2"/>
                  </svg>
                  Download Resume
                </a>
              </div>

              {/* Decorative gold vertical line */}
              <div style={{
                position: 'absolute', top: 0, left: 0, bottom: 0, width: '2px',
                background: 'linear-gradient(to bottom, transparent, #c9a96e, transparent)',
                opacity: 0.3,
              }} />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <style>{`
        .nav-desktop { display: flex !important; }
        .nav-mobile-btn { display: none !important; }

        @media (max-width: 900px) {
          .nav-desktop { display: none !important; }
          .nav-mobile-btn { display: flex !important; }
          nav { padding: 20px 24px !important; }
        }

        .nav-mobile-btn:hover .btn-bg { opacity: 1 !important; }
      `}</style>
    </>
  );
}